// ============================================================================
// Дизайн-система — управление темой (ds.theme)
// ============================================================================
// ВСЯ логика темы живёт здесь. API:
//   ds.theme.mode          — 'light' | 'dark' | 'system' (реактивно)
//   ds.theme.set('dark')   — установить + персист (cookie + Preferences)
//   ds.theme.toggle()      — переключить light/dark
//   ds.theme.init()        — клиентская инициализация (вызывает App.vue)
//
// Хранилище значения — слот store.core.state.theme (per-request изоляция SSR
// через AsyncLocalStorage + гидрация уже решены механикой стора; SSR-рендерер
// vue-app-renderer.js читает cookie и пишет сюда же). Анти-flash каскад:
//   SSR  — htmlAttrs с data-theme рендерит сервер (vue-app-renderer.js);
//   SPA  — блокирующий скрипт в <head> (builder/templates/page.js) читает cookie
//          до отрисовки.
import { Preferences } from '@capacitor/preferences';

import { useStore } from '../../modules/core/views/store/core.store.js';

const state = () => useStore().core.state.theme;

export const theme = {
  // Текущий режим (реактивен: читает reactive-state активного стора)
  get mode() {
    return state().mode;
  },

  // Theme: 'light' | 'dark' | 'system'
  async set(mode) {
    state().mode = mode;

    // Cookie/Preferences ТОЛЬКО для явного выбора light/dark
    // system — не сохраняем, пусть работает CSS media query
    if (mode === 'light' || mode === 'dark') {
      if (typeof document !== 'undefined') {
        document.cookie = `theme=${mode}; path=/; max-age=31536000; SameSite=Lax`;
      }
      await Preferences.set({ key: 'theme', value: mode });
    } else {
      // system — удаляем cookie чтобы SSR не фиксировал тему
      if (typeof document !== 'undefined') {
        document.cookie = `theme=; path=/; max-age=0`;
      }
      await Preferences.remove({ key: 'theme' });
    }

    if (typeof document === 'undefined') return;
    const root = document.documentElement;

    // Убираем старые атрибуты
    root.classList.remove('dark-theme', 'light-theme');
    root.removeAttribute('data-theme');

    if (mode === 'dark') {
      root.classList.add('dark-theme');
      root.setAttribute('data-theme', 'dark');
      root.setAttribute('color-scheme', 'dark');
    } else if (mode === 'light') {
      root.classList.add('light-theme');
      root.setAttribute('data-theme', 'light');
      root.setAttribute('color-scheme', 'light');
    } else {
      // system - без классов, CSS media query сработает
      root.setAttribute('color-scheme', 'light dark');
    }
  },

  toggle() {
    // Прямое переключение между light и dark
    const isDark = this.mode === 'dark' ||
      (this.mode === 'system' && typeof window !== 'undefined' &&
       window.matchMedia('(prefers-color-scheme: dark)').matches);
    this.set(isDark ? 'light' : 'dark');
  },

  // Клиентская инициализация (App.vue, client-only хук):
  // читает источник истины (Preferences на мобилке / SSR-атрибут в вебе)
  // и подписывается на смену системной темы.
  async init() {
    if (process.env.MOBILE_APP) {
      // Для Mobile (Capacitor) - читаем из Preferences
      const { value } = await Preferences.get({ key: 'theme' });
      await this.set(value || 'system');
    } else if (typeof document !== 'undefined') {
      // Для SSR/SPA - инициализируем state из уже проставленного SSR атрибута
      const dataTheme = document.documentElement.getAttribute('data-theme');
      state().mode = dataTheme === 'dark' ? 'dark' : dataTheme === 'light' ? 'light' : 'system';
    }

    // Слушаем изменения системной темы
    if (typeof window !== 'undefined') {
      window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', () => {
        // При изменении системной темы, если режим 'system' - обновляем CSS
        if (this.mode === 'system') this.set('system');
      });
    }
  },
};
